import React, { useState, useMemo, useEffect, useRef } from "react";
import { Stage, Layer, Rect, Text, Line, Circle, Group } from "react-konva";

export default function ExhibitionFloorPlan() {
  // Stall data (boxes representing stalls on the exhibition floor)
  // Added more stalls for detail, arranged in a larger grid-like layout with varied sizes and positions
  const stageRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const lastPointerPosition = useRef({ x: 0, y: 0 });
  const lastDist = useRef(0);

  // Mouse/Touch events
  const handlePointerDown = (e) => {
    if (e.evt.button && e.evt.button !== 0) return;

    setIsDragging(true);
    lastPointerPosition.current = {
      x: e.evt.clientX || e.evt.touches[0].clientX,
      y: e.evt.clientY || e.evt.touches[0].clientY,
    };
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;

    const stage = stageRef.current;
    const pos = stage.position();
    const scale = stage.scaleX();

    const currentPointerPos = {
      x: e.evt.clientX || (e.evt.touches && e.evt.touches[0].clientX),
      y: e.evt.clientY || (e.evt.touches && e.evt.touches[0].clientY),
    };

    if (!currentPointerPos.x || !currentPointerPos.y) return;

    const dx = currentPointerPos.x - lastPointerPosition.current.x;
    const dy = currentPointerPos.y - lastPointerPosition.current.y;

    const newPos = {
      x: pos.x + dx,
      y: pos.y + dy,
    };

    stage.position(newPos);
    stage.batchDraw();

    lastPointerPosition.current = currentPointerPos;
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    lastDist.current = 0;
  };

  // Touch pinch zoom
  const handleTouchMove = (e) => {
    e.evt.preventDefault();

    if (e.evt.touches.length === 2) {
      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];

      const dist = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );

      if (lastDist.current === 0) {
        lastDist.current = dist;
        return;
      }

      const stage = stageRef.current;
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };

      const scaleBy = dist / lastDist.current;
      const newScale = oldScale * scaleBy;

      // Apply scale constraints
      const clampedScale = Math.max(0.1, Math.min(5, newScale));

      stage.scale({ x: clampedScale, y: clampedScale });

      const newPos = {
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      };

      stage.position(newPos);
      stage.batchDraw();

      lastDist.current = dist;
    }
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();

    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const scaleBy = 1.1;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    const clampedScale = Math.max(0.1, Math.min(5, newScale));

    stage.scale({ x: clampedScale, y: clampedScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    stage.position(newPos);
    stage.batchDraw();
  };

  const [stalls] = useState([
    {
      id: "S1",
      name: "Stall 1: Tech Gadgets",
      x: 50,
      y: 50,
      width: 120,
      height: 90,
      fill: "#E6F4EA",
    },
    {
      id: "S2",
      name: "Stall 2: Fashion Wear",
      x: 200,
      y: 50,
      width: 100,
      height: 90,
      fill: "#E8F0FE",
    },
    {
      id: "S3",
      name: "Stall 3: Food Samples",
      x: 330,
      y: 50,
      width: 110,
      height: 90,
      fill: "#FFF4E5",
    },
    {
      id: "S4",
      name: "Stall 4: Books & Media",
      x: 470,
      y: 50,
      width: 130,
      height: 90,
      fill: "#FCE8E6",
    },
    {
      id: "S5",
      name: "Stall 5: Home Decor",
      x: 50,
      y: 180,
      width: 120,
      height: 100,
      fill: "#F3E8FF",
    },
    {
      id: "S6",
      name: "Stall 6: Electronics",
      x: 200,
      y: 180,
      width: 100,
      height: 100,
      fill: "#E8F5FE",
    },
    {
      id: "S7",
      name: "Stall 7: Health Products",
      x: 330,
      y: 180,
      width: 110,
      height: 100,
      fill: "#E6F4EA",
    },
    {
      id: "S8",
      name: "Stall 8: Art Gallery",
      x: 470,
      y: 180,
      width: 130,
      height: 100,
      fill: "#E8F0FE",
    },
    {
      id: "S9",
      name: "Stall 9: Toys & Games",
      x: 50,
      y: 320,
      width: 120,
      height: 90,
      fill: "#FFF4E5",
    },
    {
      id: "S10",
      name: "Stall 10: Beauty Care",
      x: 200,
      y: 320,
      width: 100,
      height: 90,
      fill: "#FCE8E6",
    },
    {
      id: "S11",
      name: "Stall 11: Sports Gear",
      x: 330,
      y: 320,
      width: 110,
      height: 90,
      fill: "#F3E8FF",
    },
    {
      id: "S12",
      name: "Stall 12: Auto Accessories",
      x: 470,
      y: 320,
      width: 130,
      height: 90,
      fill: "#E8F5FE",
    },
  ]);

  // State for selecting start and end stalls
  const [startStallId, setStartStallId] = useState(stalls[0].id);
  const [endStallId, setEndStallId] = useState(stalls[3].id);

  // Find start and end stalls
  const startStall = useMemo(
    () => stalls.find((s) => s.id === startStallId),
    [stalls, startStallId]
  );
  const endStall = useMemo(
    () => stalls.find((s) => s.id === endStallId),
    [stalls, endStallId]
  );

  // Canvas dimensions (increased for more stalls)
  const stageWidth = 650;
  const stageHeight = 450;

  // Grid for pathfinding (to avoid passing through stalls)
  const cellSize = 10;
  const gridWidth = Math.ceil(stageWidth / cellSize);
  const gridHeight = Math.ceil(stageHeight / cellSize);

  const grid = useMemo(() => {
    const g = Array.from({ length: gridHeight }, () =>
      Array(gridWidth).fill(false)
    );
    for (let stall of stalls) {
      const minX = Math.floor(stall.x / cellSize);
      const maxX = Math.ceil((stall.x + stall.width) / cellSize);
      const minY = Math.floor(stall.y / cellSize);
      const maxY = Math.ceil((stall.y + stall.height) / cellSize);
      for (let y = minY; y < maxY; y++) {
        for (let x = minX; x < maxX; x++) {
          if (x >= 0 && x < gridWidth && y >= 0 && y < gridHeight) {
            g[y][x] = true; // blocked
          }
        }
      }
    }
    return g;
  }, [stalls]);

  // Helper: Get exit/entrance points on stall sides (right, left, top, or bottom edge, middle)
  const getStallExitPoint = (stall, side) => {
    const midX = stall.x + stall.width / 2;
    const midY = stall.y + stall.height / 2;
    switch (side) {
      case "right":
        return { x: stall.x + stall.width, y: midY };
      case "left":
        return { x: stall.x, y: midY };
      case "top":
        return { x: midX, y: stall.y };
      case "bottom":
        return { x: midX, y: stall.y + stall.height };
      default:
        return { x: stall.x + stall.width, y: midY };
    }
  };

  // Adjust point to be slightly outside the stall (in the aisle)
  const adjustPointOutward = (point, side) => {
    const offset = 1; // Small offset to place in aisle
    switch (side) {
      case "right":
        point.x += offset;
        break;
      case "left":
        point.x -= offset;
        break;
      case "top":
        point.y -= offset;
        break;
      case "bottom":
        point.y += offset;
        break;
    }
    return point;
  };

  // Path routing using A* to avoid stalls, moving along sides/aisles
  const pathPoints = useMemo(() => {
    if (!startStall || !endStall || startStall.id === endStall.id) return [];

    // Determine sides based on relative positions
    let startSide = "right";
    let endSide = "left";
    if (startStall.x > endStall.x) {
      startSide = "left";
      endSide = "right";
    } else if (startStall.x === endStall.x) {
      startSide = startStall.y < endStall.y ? "bottom" : "top";
      endSide = startStall.y < endStall.y ? "top" : "bottom";
    } else if (startStall.y > endStall.y) {
      const dy = Math.abs(startStall.y - endStall.y);
      const dx = Math.abs(startStall.x - endStall.x);
      if (dy > dx) {
        startSide = "top";
        endSide = "bottom";
      }
    } else if (startStall.y < endStall.y) {
      const dy = Math.abs(startStall.y - endStall.y);
      const dx = Math.abs(startStall.x - endStall.x);
      if (dy > dx) {
        startSide = "bottom";
        endSide = "top";
      }
    }

    let startPoint = getStallExitPoint(startStall, startSide);
    startPoint = adjustPointOutward(startPoint, startSide);
    let endPoint = getStallExitPoint(endStall, endSide);
    endPoint = adjustPointOutward(endPoint, endSide);

    // Convert to grid coordinates
    const startGrid = {
      x: Math.floor(startPoint.x / cellSize),
      y: Math.floor(startPoint.y / cellSize),
    };
    const endGrid = {
      x: Math.floor(endPoint.x / cellSize),
      y: Math.floor(endPoint.y / cellSize),
    };

    // If start or end grid is blocked (shouldn't be), find nearest walkable (but assume ok)

    // A* pathfinding
    class PriorityQueue {
      constructor() {
        this.elements = [];
      }
      put(priority, item) {
        this.elements.push({ priority, item });
        this.elements.sort((a, b) => a.priority - b.priority);
      }
      get() {
        return this.elements.shift().item;
      }
      empty() {
        return this.elements.length === 0;
      }
    }

    function heuristic(a, b) {
      return Math.abs(a.x - b.x) + Math.abs(a.y - b.y); // Manhattan distance
    }

    function findPath(startG, endG) {
      const cameFrom = {};
      const gScore = {};
      gScore[`${startG.x},${startG.y}`] = 0;
      const fScore = {};
      fScore[`${startG.x},${startG.y}`] = heuristic(startG, endG);
      const openSet = new PriorityQueue();
      openSet.put(0, startG);

      while (!openSet.empty()) {
        const current = openSet.get();
        if (current.x === endG.x && current.y === endG.y) {
          // Reconstruct path
          let curr = current;
          const p = [];
          while (curr) {
            p.unshift(curr);
            curr = cameFrom[`${curr.x},${curr.y}`];
          }
          return p;
        }

        const neighbors = [
          { x: current.x + 1, y: current.y },
          { x: current.x - 1, y: current.y },
          { x: current.x, y: current.y + 1 },
          { x: current.x, y: current.y - 1 },
        ];

        for (let neighbor of neighbors) {
          if (
            neighbor.x < 0 ||
            neighbor.x >= gridWidth ||
            neighbor.y < 0 ||
            neighbor.y >= gridHeight ||
            grid[neighbor.y][neighbor.x]
          )
            continue; // blocked or out of bounds

          const tentG = gScore[`${current.x},${current.y}`] + 1;
          const key = `${neighbor.x},${neighbor.y}`;
          if (tentG < (gScore[key] ?? Infinity)) {
            cameFrom[key] = current;
            gScore[key] = tentG;
            fScore[key] = tentG + heuristic(neighbor, endG);
            openSet.put(fScore[key], neighbor);
          }
        }
      }
      return []; // no path
    }

    const gridPath = findPath(startGrid, endGrid);
    if (gridPath.length === 0) return []; // No path found

    // Convert grid path to actual points (center of cells)
    const points = gridPath.map((p) => ({
      x: p.x * cellSize + cellSize / 2,
      y: p.y * cellSize + cellSize / 2,
    }));

    // Prepend exact start and append exact end for precise connection
    if (
      Math.hypot(points[0].x - startPoint.x, points[0].y - startPoint.y) <
      cellSize * 2
    ) {
      points.unshift(startPoint);
    }
    if (
      Math.hypot(
        points[points.length - 1].x - endPoint.x,
        points[points.length - 1].y - endPoint.y
      ) <
      cellSize * 2
    ) {
      points.push(endPoint);
    }

    return points;
  }, [startStall, endStall, grid]);

  // Flatten points for Konva Line
  const flattenedPoints = pathPoints.flatMap((p) => [p.x, p.y]);

  // --- Animation: Traveling object (dot) moving from one end to the other ---
  const [travel, setTravel] = useState(0); // 0 to 1 progress along the path
  const speed = 100; // Pixels per second
  const lastTimeRef = useRef(null);

  // Compute total path length and segment lengths
  const { totalLength, segments } = useMemo(() => {
    let total = 0;
    const segs = [];
    for (let i = 0; i < pathPoints.length - 1; i++) {
      const p = pathPoints[i];
      const q = pathPoints[i + 1];
      const len = Math.hypot(q.x - p.x, q.y - p.y);
      segs.push({ from: p, to: q, len });
      total += len;
    }
    return { totalLength: total, segments: segs };
  }, [pathPoints]);

  // Function to get position along the path at a given progress (0-1)
  const getPositionAlongPath = (progress) => {
    if (!segments.length) return { x: 0, y: 0 };
    const distance = progress * (totalLength || 1);
    let accumulated = 0;
    for (const seg of segments) {
      if (
        distance <= accumulated + seg.len ||
        seg === segments[segments.length - 1]
      ) {
        const localProgress = Math.max(
          0,
          Math.min(1, (distance - accumulated) / (seg.len || 1))
        );
        return {
          x: seg.from.x + (seg.to.x - seg.from.x) * localProgress,
          y: seg.from.y + (seg.to.y - seg.from.y) * localProgress,
        };
      }
      accumulated += seg.len;
    }
    return segments[segments.length - 1].to;
  };

  // Animate the traveling dot
  useEffect(() => {
    let raf;
    const animate = (timestamp) => {
      if (lastTimeRef.current == null) lastTimeRef.current = timestamp;
      const deltaTime = (timestamp - lastTimeRef.current) / 1000; // Seconds
      lastTimeRef.current = timestamp;
      const pixels = speed * deltaTime;
      const progressIncrement = totalLength > 0 ? pixels / totalLength : 0;
      setTravel((prev) => {
        let next = prev + progressIncrement;
        if (next > 1) next = 0; // Loop back to start
        return next;
      });
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [totalLength]);

  const dotPosition = getPositionAlongPath(travel);

  // Handle stall click to select start/end
  const [selectionStep, setSelectionStep] = useState("start"); // "start" or "end"
  const handleStallClick = (stall) => {
    if (selectionStep === "start") {
      setStartStallId(stall.id);
      setSelectionStep("end");
    } else {
      setEndStallId(stall.id);
      setSelectionStep("start");
    }
  };

  return (
    <div>
      <div>
        <span>
          Click stalls to set <b>{selectionStep}</b> point
        </span>
        <div>
          <label className="text-sm">Start Stall:</label>
          <select
            value={startStallId}
            onChange={(e) => setStartStallId(e.target.value)}
          >
            {stalls.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>End Stall:</label>
          <select
            value={endStallId || ""}
            onChange={(e) => setEndStallId(e.target.value)}
          >
            <option value="">Select End</option>
            {stalls.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Stage
        width={stageWidth}
        height={stageHeight}
        onWheel={handleWheel}
        onMouseDown={handlePointerDown}
        onMousemove={handlePointerMove}
        onMouseup={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={(e) => {
          handlePointerMove(e);
          handleTouchMove(e);
        }}
        onTouchEnd={handlePointerUp}
        draggable={false}
        ref={stageRef}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <Layer>
          <Rect
            x={0}
            y={0}
            width={stageWidth}
            height={stageHeight}
            fill="lightblue"
            listening={false}
          />
          {/* Stalls as detailed boxes with names */}
          {stalls.map((stall) => (
            <Group key={stall.id} onClick={() => handleStallClick(stall)}>
              <Rect
                x={stall.x}
                y={stall.y}
                width={stall.width}
                height={stall.height}
                fill={stall.fill}
                stroke={
                  stall.id === startStallId
                    ? "#16A34A"
                    : stall.id === endStallId
                    ? "#2563EB"
                    : "#CBD5E1"
                }
                strokeWidth={
                  stall.id === startStallId || stall.id === endStallId ? 3 : 1
                }
                cornerRadius={8}
                shadowBlur={
                  stall.id === startStallId || stall.id === endStallId ? 10 : 0
                }
                shadowColor="#94A3B8"
              />
              <Text
                x={stall.x + 10}
                y={stall.y + 10}
                width={stall.width - 20}
                text={stall.name}
                fontSize={12}
                fill="#334155"
                wrap="word"
                align="center"
              />
            </Group>
          ))}

          {/* Path line along sides/aisles (dashed for visual interest) */}
          {flattenedPoints.length >= 4 && (
            <Line
              points={flattenedPoints}
              stroke="#111827"
              strokeWidth={4}
              lineCap="round"
              lineJoin="round"
              dash={[10, 5]} // Dashed line for more detail
              opacity={0.8}
            />
          )}

          {/* Animated moving object (dot) */}
          {segments.length > 0 && (
            <Circle
              x={dotPosition.x}
              y={dotPosition.y}
              radius={7}
              fill="#22C55E"
              shadowBlur={12}
              shadowColor="#22C55E"
            />
          )}
        </Layer>
      </Stage>

      <p>
        Tip: Paths now use A* pathfinding to avoid passing through stalls,
        navigating along sides and aisles. The green dot animates along the
        path, looping from start to end.
      </p>
    </div>
  );
}
